import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SharedModule } from 'src/app/theme/shared/shared.module';
import { NotificationService } from './notification.service';

export interface ApiNotification {
  _id?: string;
  title: string;
  body: string;
  type: string;
  notificationType: string;
  is_read: boolean;
  meta_data: { key: string; value: string }[];
  date_created_utc: string;
}

type Tab = 'all' | 'unread';

@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.scss'
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private svc    = inject(NotificationService);
  private cdr    = inject(ChangeDetectorRef);
  private router = inject(Router);
  private zone   = inject(NgZone);

  notifications: ApiNotification[] = [];
  unreadCount = 0;
  tab: Tab = 'all';

  page        = 1;
  readonly limit = 10;
  totalPages  = 1;
  loading     = false;
  loadingMore = false;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadFirst();

    // Reload list when dropdown opens
    this.svc.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadFirst());

    // Prepend incoming socket notifications
    this.svc.newNotif$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data?.type !== 'ADMIN') return;
      const notif: ApiNotification = {
        ...data,
        _id: data._id ?? `socket-${Date.now()}`,
        is_read: false,
      };
      this.notifications = [notif, ...this.notifications];
      this.unreadCount = this.svc.getUnreadCount();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  switchTab(t: Tab): void {
    if (this.tab === t) return;
    this.tab = t;
    this.loadFirst();
  }

  loadFirst(): void {
    this.page = 1;
    this.notifications = [];
    this.loading = true;
    this.cdr.detectChanges();

    const is_read = this.tab === 'unread' ? false : undefined;
    this.svc.getList(1, this.limit, is_read).subscribe({
      next: (res) => {
        const d = res?.data ?? {};
        this.unreadCount   = d.unreadCount ?? 0;
        this.notifications = d.notifications ?? [];
        this.totalPages    = d.pagination?.pages ?? 1;
        this.svc.setUnreadCount(this.unreadCount);
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loading = false; this.cdr.detectChanges(); }
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - 60 &&
      !this.loadingMore &&
      this.page < this.totalPages
    ) {
      this.loadMore();
    }
  }

  private loadMore(): void {
    this.page++;
    this.loadingMore = true;
    this.cdr.detectChanges();

    const is_read = this.tab === 'unread' ? false : undefined;
    this.svc.getList(this.page, this.limit, is_read).subscribe({
      next: (res) => {
        const d = res?.data ?? {};
        this.notifications = [...this.notifications, ...(d.notifications ?? [])];
        this.totalPages    = d.pagination?.pages ?? this.totalPages;
        this.loadingMore   = false;
        this.cdr.detectChanges();
      },
      error: () => { this.loadingMore = false; this.cdr.detectChanges(); }
    });
  }

  markAllRead(): void {
    this.svc.markAllRead().subscribe({
      next: () => {
        this.notifications.forEach(n => n.is_read = true);
        this.unreadCount = 0;
        this.svc.setUnreadCount(0);
        this.cdr.detectChanges();
      }
    });
  }

  markRead(n: ApiNotification): void {
    if (!n.is_read) {
      n.is_read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.svc.setUnreadCount(this.unreadCount);
      this.cdr.detectChanges();

      if (n._id && !n._id.startsWith('socket-')) {
        this.svc.markOneRead(n._id).subscribe();
      }
    }
    this.navigateTo(n);
  }

  private navigateTo(n: ApiNotification): void {
    const meta = n.meta_data ?? [];
    const driverId = meta.find(m => m.key === 'driverId')?.value;
    if (!driverId) return;

    if (n.notificationType === 'driver_vehicle_added' || n.notificationType === 'driver_document_uploaded') {
      // Run inside NgZone — socket-fed notifications can leave this handler
      // outside Angular's zone, which updates the URL but never renders the route.
      this.zone.run(() => this.router.navigate(['/drivers/profile', driverId, 'vehicles-docs']));
    }
  }

  getIcon(type: string): { icon: string; bg: string } {
    if (!type) return { icon: 'ti ti-bell', bg: 'notif-icon--blue' };
    if (type.includes('vehicle'))  return { icon: 'ti ti-car',       bg: 'notif-icon--amber' };
    if (type.includes('driver'))   return { icon: 'ti ti-user',      bg: 'notif-icon--blue'  };
    if (type.includes('trip'))     return { icon: 'ti ti-route',     bg: 'notif-icon--green' };
    if (type.includes('payment'))  return { icon: 'ti ti-cash',      bg: 'notif-icon--green' };
    if (type.includes('document')) return { icon: 'ti ti-file-text', bg: 'notif-icon--amber' };
    return { icon: 'ti ti-bell', bg: 'notif-icon--blue' };
  }
}
